import { useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  CardHeader,
  DataGrid,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  Loading,
  Radio,
  RadioGroup,
  Tooltip,
} from "../../../../components";
import {
  ConfiguracoesService,
  ContasPagarService,
  ContasReceberService,
} from "../../../../services";
import { formatDate, formatPrice, responseErros } from "../../../../utils";
import { format } from "date-fns";
import {
  calculaJurosBaixa,
  calculaMultaBaixa,
  calculaMultaJurosBaixa,
  jurosMultaDoTitulo,
} from "../../../contas-receber/list-contas-receber/list-contas-receber";
import { getAtrasoDias } from "../../../contas-receber/add-contas-receber/add-contas-receber";

function HistoricoContas({
  openHistorico,
  setOpenHistorico,
  handleHistorico,
  pessoa,
}) {
  const contasPagarService = new ContasPagarService();
  const contasReceberService = new ContasReceberService();
  const configuracoesService = new ConfiguracoesService();
  const [tipoBusca, setTipoBusca] = useState("receber");
  const [totalizadores, setTotalizadores] = useState({
    vencidas: 0,
    aVencer: 0,
    hoje: 0,
    totalRecebido: 0,
  });
  const [totalizadoresPorTipo, setTotalizadoresPorTipo] = useState({
    pagar: { vencidas: 0, aVencer: 0, hoje: 0, totalRecebido: 0 },
    receber: { vencidas: 0, aVencer: 0, hoje: 0, totalRecebido: 0 },
  });
  const [jurosMultaEmpresas, setJurosMultaEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contas, setContas] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const initialLoad = useRef(true);

  useEffect(() => {
    if (openHistorico) {
      if (pessoa?.tiposPessoa?.length) {
        const isCliente = pessoa.tiposPessoa.some(
          (tipoPessoa) => tipoPessoa.id === 1
        );
        setTipoBusca(!isCliente ? "pagar" : "receber");
      }
      if (initialLoad.current) {
        buscarTodosTotalizadores();
        initialLoad.current = false;
      } else {
        setTotalizadores(totalizadoresPorTipo[tipoBusca]);
      }
      buscarContas();
    }
  }, [openHistorico, pessoa, tipoBusca]);

  const buscarTodosTotalizadores = async () => {
    setLoading(true);
    try {
      const [resPagar, resReceber] = await Promise.all([
        contasPagarService.getResumoHistoricoContasCliente(pessoa?.id),
        contasReceberService.getResumoHistoricoContasCliente(pessoa?.id),
      ]);

      let totalPagar = { vencidas: 0, aVencer: 0, hoje: 0, totalRecebido: 0 };
      if (resPagar && !resPagar.isAxiosError) {
        totalPagar = resPagar.data;
      } else if (resPagar) {
        responseErros(resPagar);
      }

      let totalReceber = { vencidas: 0, aVencer: 0, hoje: 0, totalRecebido: 0 };
      if (resReceber && !resReceber.isAxiosError) {
        totalReceber = resReceber.data;
      } else if (resReceber) {
        responseErros(resReceber);
      }

      const novosTotalizadores = {
        pagar: totalPagar,
        receber: totalReceber,
      };

      setTotalizadoresPorTipo(novosTotalizadores);
      setTotalizadores(novosTotalizadores[tipoBusca]);
    } catch (error) {
      responseErros(error);
    } finally {
      setLoading(false);
    }
  };

  const colunasPagar = [
    {
      field: "status",
      headerName: "Status",
      flex: 100,
      renderCell: (params) => {
        switch (params.value) {
          case "BAIXADO":
            return (
              <Tooltip title="Baixado" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#004780", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          case "BAIXADO_PARCIAL":
            return (
              <Tooltip title="Baixado Parcial" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#EA9D2B", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          case "CANCELADO":
            return (
              <Tooltip title="Cancelado" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#DC3545", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          default:
            return (
              <Tooltip title="Aberto" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#4FBB53", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
        }
      },
    },
    {
      field: "numeroTitulo",
      headerName: "Nº Título",
      flex: 230,
      filtrar: true,
    },
    {
      field: "tipo",
      headerName: "Tipo",
      flex: 250,
      valueGetter: (params) =>
        params.value == "TITULO_NORMAL" ? "Título normal" : "Nota de débito",
    },
    {
      field: "valor",
      headerName: "Valor Título",
      flex: 320,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value),
    },
    {
      field: "dataVencimento",
      headerName: "Data de Vencimento",
      flex: 250,
      valueGetter: (params) =>
        format(formatDate.received(params?.value), "dd/MM/yyyy"),
    },
  ];

  const colunasReceber = [
    {
      field: "status",
      headerName: "Status",
      flex: 100,
      renderCell: (params) => {
        switch (params.value) {
          case "BAIXADO":
            return (
              <Tooltip title="Baixado" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#004780", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          case "BAIXADO_PARCIAL":
            return (
              <Tooltip title="Baixado Parcial" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#EA9D2B", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          case "CANCELADO":
            return (
              <Tooltip title="Cancelado" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#DC3545", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          default:
            return (
              <Tooltip title="Aberto" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#4FBB53", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
        }
      },
    },
    {
      field: "numeroTitulo",
      headerName: "Nº Título",
      flex: 230,
      filtrar: true,
    },
    {
      field: "tipo",
      headerName: "Tipo",
      flex: 250,
      valueGetter: (params) =>
        params.value == "TITULO_NORMAL" ? "Título normal" : "Nota de crédito",
    },
    {
      field: "valor",
      headerName: "Valor Título",
      flex: 320,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value),
    },
    {
      field: "atraso",
      headerName: "Atraso",
      flex: 200,
      valueGetter: (params) => {
        if (
          params.row.status === "BAIXADO" ||
          params.row.status === "CANCELADO"
        ) {
          return "Em dia";
        }
        const atraso = getAtrasoDias(
          params.row.dataVencimento,
          new Date().toISOString()
        );
        return atraso > 0 ? `${atraso} dias` : "Em dia";
      },
    },
    {
      field: "multa",
      headerName: "Multa",
      flex: 200,
      valueGetter: (params) => {
        const multa = calculaMultaBaixa(
          params.row,
          jurosMultaDoTitulo(jurosMultaEmpresas, params.row),
          new Date().toISOString()
        );
        return formatPrice(multa);
      },
    },
    {
      field: "juros",
      headerName: "Juros",
      flex: 200,
      valueGetter: (params) => {
        const juros = calculaJurosBaixa(
          params.row,
          jurosMultaDoTitulo(jurosMultaEmpresas, params.row),
          new Date().toISOString()
        );
        return formatPrice(juros);
      },
    },
    {
      field: "saldoReceber",
      headerName: "Saldo a receber",
      flex: 300,
      sortable: false,
      valueGetter: (params) =>
        params.value > 0 && params.row.status !== "CANCELADO"
          ? formatPrice(
              calculaMultaJurosBaixa(
                params.row,
                jurosMultaDoTitulo(jurosMultaEmpresas, params.row)
              )
            )
          : "R$ 0,00",
    },
    {
      field: "dataVencimento",
      headerName: "Data de Vencimento",
      flex: 250,
      valueGetter: (params) =>
        format(formatDate.received(params?.value), "dd/MM/yyyy"),
    },
  ];

  const handleChangeTipoOperacao = (e) => {
    const novoTipo = e.target.value;
    setTipoBusca(novoTipo);
    setTotalizadores(totalizadoresPorTipo[novoTipo]);
  };

  const buscarContas = async (filtros = {}) => {
    setLoading(true);
    await buscarJurosMulta();
    try {
      let result = {};
      if (tipoBusca == "receber") {
        const filtro = { ...filtros, pagadorId: pessoa?.id };
        result = await contasReceberService.getAllFiltroAvancado(filtro);
      } else {
        const filtro = { ...filtros, pessoaId: pessoa?.id };
        result = await contasPagarService.getAllFiltroAvancado(filtro);
      }
      if (!result.isAxiosError) {
        setContas(result.data.rows);
        setRowCount(result.data.count);
      } else {
        responseErros(result);
      }
    } catch (error) {
      responseErros(error);
    } finally {
      setLoading(false);
    }
  };

  const buscarJurosMulta = async () => {
    if (jurosMultaEmpresas.length > 0) return;

    try {
      const result = await configuracoesService.getJurosMulta();
      if (!result.isAxiosError) {
        setJurosMultaEmpresas(result.data);
      }
    } catch (error) {
      responseErros(error);
    }
  };

  const handleCloseDialog = () => {
    setOpenHistorico(false);
    setContas([]);
  };

  const handlePesquisaAvancada = async (filter) => {
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.dataLancamentoStart &&
      filtrosAvancadosTemp.dataLancamentoEnd
    ) {
      porIntervalo.push({
        coluna: "dataLancamento",
        de: filtrosAvancadosTemp.dataLancamentoStart,
        ate: filtrosAvancadosTemp.dataLancamentoEnd,
      });
    }
    if (
      filtrosAvancadosTemp.dataVencimentoStart &&
      filtrosAvancadosTemp.dataVencimentoEnd
    ) {
      porIntervalo.push({
        coluna: "dataVencimento",
        de: filtrosAvancadosTemp.dataVencimentoStart,
        ate: filtrosAvancadosTemp.dataVencimentoEnd,
      });
    }
    delete filtrosAvancadosTemp.dataLancamentoStart;
    delete filtrosAvancadosTemp.dataLancamentoEnd;
    delete filtrosAvancadosTemp.dataVencimentoStart;
    delete filtrosAvancadosTemp.dataVencimentoEnd;
    let result = {};
    if (tipoBusca === "receber") {
      result = await contasReceberService.getAllFiltroAvancado({
        ...filtrosAvancadosTemp,
        restritiva: true,
        porIntervalo,
      });
    } else {
      result = await contasPagarService.getAllFiltroAvancado({
        ...filtrosAvancadosTemp,
        restritiva: true,
        porIntervalo,
      });
    }
    if (!result.isAxiosError) {
      setContas(result.data.rows);
      setRowCount(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    const { filtros, tipoFiltro } = props;
    if (tipoFiltro === "simples") {
      buscarContas(filtros);
    } else {
      handlePesquisaAvancada(filtros);
    }
  };

  const CardTotalizadores = ({ label, value, color, tip }) => {
    return (
      <Grid item xs={2.5}>
        <Tooltip title={tip} arrow placement="top">
          <Card
            sx={{
              padding: 2,
              borderRadius: 2,
              borderLeft: color ? `7px solid ${color}` : null,
              boxShadow: 4,
            }}
          >
            <div className="d-flex flex-column align-items-center">
              <span>{label}</span>
              <br />
              <strong style={{ fontSize: "22px", color }}>
                {formatPrice(value)}
              </strong>
            </div>
          </Card>
        </Tooltip>
      </Grid>
    );
  };

  const cardsTotal = [
    {
      label: "Vencidas",
      cor: "#E44A42",
      key: "vencidas",
      tip: "Duplicatas vencidas e não quitadas",
    },
    {
      label: "Vencidas Hoje",
      cor: "#E44A42",
      key: "hoje",
      tip: "Duplicatas vencidas e não quitadas com vencimento hoje",
    },
    {
      label: "A Vencer",
      cor: "#2196F3",
      key: "aVencer",
      tip: "Duplicatas com data de vencimento futura",
    },
    {
      label: tipoBusca === "receber" ? "Total Recebido" : "Total Pago",
      cor: "#4fbb53",
      key: tipoBusca === "receber" ? "totalRecebido" : "totalPago",
      tip: "Total de duplicatas quitadas",
    },
  ];

  return (
    <Dialog
      maxWidth={"xl"}
      fullWidth
      open={openHistorico}
      onClose={handleCloseDialog}
    >
      <DialogContent>
        <CardHeader
          title="Histórico de Contas"
          action={
            <Button
              onClick={handleHistorico}
              variant="contained"
              color="padrao"
            >
              fechar
            </Button>
          }
        />
        <Grid
          container
          spacing={2}
          className="d-flex justify-content-start align-items-center mb-4"
        >
          <Grid item xs={5}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={tipoBusca}
                onChange={handleChangeTipoOperacao}
              >
                <FormControlLabel
                  value="pagar"
                  control={<Radio size="small" />}
                  label={
                    <span style={{ fontSize: "13px" }}>Contas a pagar</span>
                  }
                />
                <FormControlLabel
                  value="receber"
                  control={<Radio size="small" />}
                  label={
                    <span style={{ fontSize: "13px" }}>Contas a Receber</span>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          className="d-flex justify-content-between align-items-center mb-4"
        >
          {cardsTotal.map((card, index) => (
            <CardTotalizadores
              key={index}
              label={card.label}
              value={totalizadores[card.key]}
              color={card.cor}
              tip={card.tip}
            />
          ))}
        </Grid>
        <DataGrid
          rows={contas}
          columns={tipoBusca === "receber" ? colunasReceber : colunasPagar}
          rowCount={rowCount}
          sendServer={(props) => sendServerDatagrid(props)}
          disableRowSelectionOnClick
          disableAcoes
        />
      </DialogContent>
      <Loading open={loading} />
    </Dialog>
  );
}

export default HistoricoContas;
